import { useEffect, useRef } from "react";
import { glowColors, normalizeHex } from "@/shared/utils/color";

/**
 * O fundo da Home (2026-10-15, "Orbe"): uma gota grande de vidro líquido, com as
 * cores do tema girando por dentro, reflexo de luz e um halo suave, que respira
 * e se deforma devagar. Desenhada num shader WebGL (a "seda" de ondas saiu
 * porque lembrava outro portal da casa).
 *
 * Duas camadas de movimento, que se somam: o contorno respira e as cores giram
 * sozinhas, e a ROLAGEM leva a orbe da direita até atrás do texto, crescendo e
 * girando as cores (`u_p`, de 0 no topo a 1 depois de uma tela de rolagem, ou
 * de toda a rolagem que a página tiver, se for menos),
 * amortecida quadro a quadro pra ela seguir a página com inércia. Desenha a ~30
 * quadros por segundo em resolução reduzida (o desfoque da borda esconde), dorme
 * com a aba em segundo plano e, com "reduzir movimento", fica parada — só reage
 * à rolagem. Sem WebGL, sobra o fundo liso do painel. Decoração: fora do leitor
 * de tela.
 */
const FRAME_MS = 1000 / 30;
const RENDER_SCALE = 0.75;

const VERTEX = "attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }";

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 u_res; uniform float u_t, u_p, u_dark;
uniform vec3 u_bg, u_c1, u_c2, u_c3;

// Ruído suave sem seno gigante (o hash clássico com sin()*43758 vira degrau na
// precisão média das GPUs do Mac).
float hash(vec2 p){ vec3 q = fract(vec3(p.xyx)*0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y)*q.z); }
float noise(vec2 x){
  vec2 i = floor(x), f = fract(x); f = f*f*(3.0 - 2.0*f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

void main(){
  vec2 uv = gl_FragCoord.xy/u_res; float ax = u_res.x/u_res.y; vec2 P = vec2(uv.x*ax, uv.y);
  // O centro começa um pouco à direita do meio da tela e, ao rolar, atravessa
  // até a esquerda, subindo um pouco.
  vec2 C = vec2(mix(0.58, 0.28, u_p)*ax, mix(0.52, 0.62, u_p) + 0.025*sin(u_t*0.35));
  vec2 d = P - C; float L = length(d);
  vec2 dir = d/max(L, 1e-4);
  // O contorno respira pela DIREÇÃO (vetor), nunca pelo ângulo: sem emenda.
  float wob = 0.06*(noise(dir*1.6 + vec2(u_t*0.22, -u_t*0.18) + u_p*1.5) - 0.5)
            + 0.025*sin(dir.x*3.0 + u_t*0.55) + 0.02*cos(dir.y*4.0 - u_t*0.7 + u_p*3.0);
  // Ao rolar, a orbe quase dobra de tamanho.
  float R = (0.5 + 0.45*u_p)*(1.0 + wob);
  float inside = 1.0 - smoothstep(R - 0.008, R + 0.008, L);
  // Uma esfera de mentira: a profundidade z dá o volume, o reflexo e a borda clara.
  vec2 nn = d/R; float z = sqrt(max(0.0, 1.0 - dot(nn, nn)));
  // A cor corre pela superfície numa direção que gira devagar (e com a rolagem).
  float a = u_t*0.12 + u_p*3.6;
  vec2 axis = vec2(cos(a), sin(a));
  float h = dot(nn, axis)*0.5 + 0.5 + 0.35*z + 0.25*(noise(nn*1.4 + u_t*0.08) - 0.5);
  vec3 col = mix(u_c1, u_c2, smoothstep(0.15, 0.85, h));
  col = mix(col, u_c3, smoothstep(0.55, 1.05, h)*0.75);
  vec3 N = normalize(vec3(nn, z + 0.001));
  float spec = pow(max(0.0, dot(N, normalize(vec3(-0.45, 0.55, 0.75)))), 24.0);
  col = mix(col, vec3(1.0), pow(1.0 - z, 3.0)*0.3 + spec*0.55);
  float alpha = inside*(u_dark > 0.5 ? 0.42 : 0.55);
  float glow = exp(-max(L - R, 0.0)*6.0)*(1.0 - inside)*(u_dark > 0.5 ? 0.14 : 0.16);
  vec3 o = mix(u_bg, col, alpha);
  o = mix(o, mix(u_c1, u_c2, 0.5), glow);
  gl_FragColor = vec4(o, 1.0);
}`;

type Rgb = [number, number, number];
type Palette = { bg: Rgb; c1: Rgb; c2: Rgb; c3: Rgb; dark: number };

/** Qualquer cor CSS (hex, rgb, color-mix já resolvido) → RGB 0..1, pelo próprio navegador. */
function makeColorReader() {
  const probe = document.createElement("canvas");
  probe.width = probe.height = 1;
  const ctx = probe.getContext("2d", { willReadFrequently: true });
  return (css: string, fallback: Rgb): Rgb => {
    if (!ctx || !css) return fallback;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = "#000";
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return [r / 255, g / 255, b / 255];
  };
}

export function HomeBackdrop() {
  const ref = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const backdrop = ref.current;
    const canvas = canvasRef.current;
    // O jsdom dos testes não tem WebGL e avisa a cada `getContext`; lá não há o que desenhar.
    if (!backdrop || !canvas || navigator.userAgent.includes("jsdom")) return;
    const gl = canvas.getContext("webgl", { antialias: false, alpha: false });
    if (!gl || gl.isContextLost()) return;

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
      // Decoração não derruba a tela, mas também não falha calada.
      console.warn("HomeBackdrop: o shader do fundo não compilou.", gl.getShaderInfoLog(shader));
      return null;
    };
    const vertex = compile(gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "a");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const U = {
      res: uniform("u_res"), t: uniform("u_t"), p: uniform("u_p"), dark: uniform("u_dark"),
      bg: uniform("u_bg"), c1: uniform("u_c1"), c2: uniform("u_c2"), c3: uniform("u_c3"),
    };

    // Cores: as do tema, e o fundo é o do próprio painel (é sobre ele que o vidro fica).
    const toRgb = makeColorReader();
    const panel = backdrop.closest<HTMLElement>(".content");
    const readPalette = (): Palette => {
      const style = getComputedStyle(backdrop);
      // As cores da orbe além do destaque são as VIVAS da paleta: os temas
      // claros têm cartões em cinza e grafite, e a luz ficava cinza.
      const accent = style.getPropertyValue("--accent").trim();
      const cards = ["--bento-card-1", "--bento-card-2", "--bento-card-3"].map(name => style.getPropertyValue(name).trim());
      const [glowA, glowB] = normalizeHex(accent)
        ? glowColors(accent, cards)
        : [style.getPropertyValue("--bento-card-3").trim(), style.getPropertyValue("--bento-card-1").trim()];
      return {
        bg: toRgb(panel ? getComputedStyle(panel).backgroundColor : "", [0.96, 0.97, 0.99]),
        c1: toRgb(accent, [0.19, 0.36, 1]),
        c2: toRgb(glowA, [0.58, 0.65, 1]),
        c3: toRgb(glowB, [0.72, 0.98, 0.31]),
        dark: document.documentElement.dataset.mode === "dark" ? 1 : 0,
      };
    };
    let palette = readPalette();

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    // O fundo cobre EXATAMENTE o painel de conteúdo (e não a janela): assim a
    // orbe é desenhada só ali e as bordas dela podem se desvanecer antes da
    // borda do painel, em vez de ser cortada seco contra a moldura do app.
    const fitToPanel = () => {
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      backdrop.style.inset = "auto";
      backdrop.style.left = `${rect.left}px`;
      backdrop.style.top = `${rect.top}px`;
      backdrop.style.width = `${rect.width}px`;
      backdrop.style.height = `${rect.height}px`;
    };
    // Mudar `canvas.width`/`height` APAGA o desenho do WebGL. Por isso só se
    // mexe quando o tamanho muda de verdade, e quem chama redesenha na hora —
    // sem isso, abrir o menu lateral (o painel muda de largura a cada quadro
    // da animação) deixava o painel em branco por um instante, piscando.
    const resize = (): boolean => {
      fitToPanel();
      const scale = Math.min(window.devicePixelRatio || 1, 1) * RENDER_SCALE;
      const width = Math.max(1, Math.round(canvas.clientWidth * scale));
      const height = Math.max(1, Math.round(canvas.clientHeight * scale));
      if (width === canvas.width && height === canvas.height) return false;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
      return true;
    };

    // Rolagem: quanto o painel rolou, em telas, e o valor amortecido que o shader recebe.
    const source: HTMLElement | Window = panel ?? window;
    // O percurso inteiro acontece na primeira tela de rolagem — ou em toda a
    // rolagem que a página tem, se ela for mais curta que isso. Medir em "telas
    // fixas" deixava a orbe quase parada numa Home que rola pouco.
    const progress = () => {
      const top = panel ? panel.scrollTop : window.scrollY;
      const room = panel ? panel.scrollHeight - panel.clientHeight : document.documentElement.scrollHeight - window.innerHeight;
      const span = Math.max(1, Math.min(room, window.innerHeight * 0.9));
      return Math.min(Math.max(top / span, 0), 1);
    };
    let current = progress();

    const start = performance.now();
    const draw = (now: number) => {
      gl.uniform2f(U.res, canvas.width, canvas.height);
      gl.uniform1f(U.t, reduced ? 0 : (now - start) / 1000);
      gl.uniform1f(U.p, current);
      gl.uniform1f(U.dark, palette.dark);
      gl.uniform3fv(U.bg, palette.bg);
      gl.uniform3fv(U.c1, palette.c1);
      gl.uniform3fv(U.c2, palette.c2);
      gl.uniform3fv(U.c3, palette.c3);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    let frame = 0;
    let last = 0;
    const loop = (now: number) => {
      const goal = progress();
      const settling = Math.abs(goal - current) > 0.0005;
      current = settling ? current + (goal - current) * 0.08 : goal;
      // Parado (reduzir movimento), o laço só roda enquanto a rolagem assenta.
      if (reduced && !settling) {
        draw(now);
        frame = 0;
        return;
      }
      frame = requestAnimationFrame(loop);
      if (now - last < FRAME_MS) return;
      last = now;
      draw(now);
    };
    const wake = () => {
      if (!frame) frame = requestAnimationFrame(loop);
    };

    const observer = new MutationObserver(() => {
      // A cor do painel muda junto com o tema, mas por transição; relê no quadro seguinte.
      requestAnimationFrame(() => {
        palette = readPalette();
        wake();
      });
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-mode", "data-theme", "style"] });
    const onResize = () => {
      if (resize()) draw(performance.now());
      wake();
    };

    resize();
    frame = requestAnimationFrame(loop);
    source.addEventListener("scroll", wake, { passive: true });
    window.addEventListener("resize", onResize);
    // O painel muda de tamanho sem a janela mudar (menu lateral abrindo).
    const panelObserver = typeof ResizeObserver === "undefined" || !panel ? null : new ResizeObserver(onResize);
    if (panel) panelObserver?.observe(panel);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      panelObserver?.disconnect();
      source.removeEventListener("scroll", wake);
      window.removeEventListener("resize", onResize);
      // O contexto NÃO é descartado aqui: o React (e a recarga do Vite) reaproveita o
      // mesmo canvas, e `getContext` devolveria o contexto já perdido — tela vazia.
    };
  }, []);

  return (
    <div ref={ref} className="home-backdrop" aria-hidden="true">
      <canvas ref={canvasRef} className="home-orb" />
    </div>
  );
}
